/**
 * CampoEditorPanel — edicao de UM campo do formulario (SlideOverPanel).
 *
 * Coleta: label, placeholder, ajuda, obrigatorio, largura FULL/HALF, lista
 * de opcoes (para SELECT/RADIO/MULTI_SELECT/CHECKBOX), limites de validacao
 * (min/max no NUMERO, caracteres no texto e telefone), mapeia_para (atributo
 * do Lead) e o CONSTRUTOR DE LOGICA CONDICIONAL (acao + modo + regras
 * referenciando outros campos).
 *
 * Estado local proprio (rascunho) — confirma via "Salvar campo" que devolve
 * o objeto-campo completo ao Editor. Campo SECAO esconde os controles de
 * input (so titulo + descricao). A condicao pode citar qualquer outro campo
 * que nao feche ciclo (`outrosCampos`, vindos de `camposCitaveis`), e cita o
 * campo pela `_key` — campo recem-criado ainda nao tem id (FORM-09).
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";

import SlideOverPanel from "../../components/SlideOverPanel";
import {
  ACOES_CONDICAO,
  MODOS_CONDICAO,
  OPERADORES_CONDICAO,
  TIPO_CAMPO_LABEL,
  destinoEhEndereco,
  operadorPrecisaValor,
  tipoAceitaMapeia,
  tipoSemInput,
  tipoTemOpcoes,
} from "../../services/formularios";

import {
  CheckLabel,
  CondicaoBox,
  EditorFieldset,
  InlineRow,
  OpcaoRow,
  PanelLabel,
  PanelSectionTitle,
  PanelStack,
  RegraRow,
  SubLabel,
} from "./styles";
import { semOpcoesRepetidas } from "./camposHelpers";
import { textoDaCondicao } from "./condicoes";
import {
  ajudaDoDestinoOcupado,
  avisoDoDestinoGravado,
  gruposDeMapeamento,
} from "./mapeamentoCampo";
import {
  LIMITES_DE_TAMANHO,
  ROTULO_DO_LIMITE,
  erroNosLimites,
  limitesDoTipo,
  limitesParaPayload,
} from "./limitesCampo";

const AJUDA_ENDERECO_PRINCIPAL =
  "Grava no endereço principal do lead (criado com o título “Principal” quando ele ainda não tem endereço).";

// So CEP e UF pedem instrucao a mais: o CEP muda o comportamento do proprio
// formulario, e a UF e' o unico destino de endereco que o servidor recusa
// quando nao reconhece o valor.
const AJUDA_POR_DESTINO_DE_ENDERECO = {
  cep: "Ao completar os 8 dígitos, o formulário busca o endereço e preenche os campos mapeados a Logradouro, Bairro, Cidade e UF.",
  estado: "Aceita a sigla (SP) ou o nome do estado; numa lista, use siglas ou nomes de estado como opções.",
};

function ajudaDoEndereco(destino) {
  const extra = AJUDA_POR_DESTINO_DE_ENDERECO[destino];
  return extra ? `${AJUDA_ENDERECO_PRINCIPAL} ${extra}` : AJUDA_ENDERECO_PRINCIPAL;
}

// Minimo que o servidor aceita em cada limite de tamanho (min_len 0, max_len 1).
const MINIMO_DO_LIMITE = { min_len: 0, max_len: 1 };

const AJUDA_DOS_LIMITES = {
  numero: "Vazio = sem limite. Quem responde vê o limite antes de enviar.",
  tamanho: "Vazio = sem limite. Os espaços do começo e do fim não contam.",
  // O telefone e' gravado com a mascara: o limite conta parenteses, espaco e
  // hifen (FORM-39).
  telefone: "Vazio = sem limite. Conta com a máscara: “(11) 99999-8888” tem 15 caracteres.",
};

function ajudaDosLimites(tipo) {
  if (tipo === "TELEFONE") return AJUDA_DOS_LIMITES.telefone;
  if (tipo === "NUMERO") return AJUDA_DOS_LIMITES.numero;
  return AJUDA_DOS_LIMITES.tamanho;
}

function entreAspas(textos) {
  return textos.map((texto) => `“${texto}”`).join(", ");
}

function CampoEditorPanel({
  open,
  campo,
  outrosCampos = [],
  destinosUsados = {},
  onClose,
  onSave,
  somenteLeitura = false,
}) {
  const [rascunho, setRascunho] = useState(null);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  // Hidrata o rascunho ao abrir (ou quando o campo alvo muda).
  useEffect(() => {
    if (!open || !campo) return;
    setRascunho({
      ...campo,
      opcoes: Array.isArray(campo.opcoes) ? [...campo.opcoes] : [],
      validacao:
        campo.validacao && typeof campo.validacao === "object" ? { ...campo.validacao } : {},
      condicao:
        campo.condicao && Array.isArray(campo.condicao.regras)
          ? {
              acao: campo.condicao.acao || "MOSTRAR",
              modo: campo.condicao.modo || "TODAS",
              regras: campo.condicao.regras.map((r) => ({ ...r })),
            }
          : null,
    });
    setErro("");
    setAviso("");
  }, [open, campo]);

  // Campos disponiveis para a condicao: os que o Editor ofereceu (qualquer
  // campo sem ciclo) e que tem input (SECAO nao gera valor para comparar).
  const camposReferenciaveis = useMemo(
    () => outrosCampos.filter((c) => !tipoSemInput(c.tipo)),
    [outrosCampos]
  );

  if (!rascunho) {
    return (
      <SlideOverPanel open={open} onClose={onClose} title="Editar campo" />
    );
  }

  const set = (patch) => setRascunho((prev) => ({ ...prev, ...patch }));
  const ehSecao = tipoSemInput(rascunho.tipo);
  const temOpcoes = tipoTemOpcoes(rascunho.tipo);
  const aceitaMapeia = tipoAceitaMapeia(rascunho.tipo);
  const temCondicao = Boolean(rascunho.condicao);
  const limites = limitesDoTipo(rascunho.tipo);
  const limitesDeTamanho = limites === LIMITES_DE_TAMANHO;
  // Destino ja usado por outro campo aparece desabilitado, com o rotulo de
  // quem o ocupa: dois campos no mesmo destino faziam o ultimo vencer e o
  // outro sumir sem aviso (FORM-23).
  const grupos = gruposDeMapeamento(rascunho.tipo, {
    destinosUsados,
    atual: rascunho.mapeia_para || "",
  });
  const ajudaDoOcupado = ajudaDoDestinoOcupado(
    rascunho.tipo, destinosUsados, rascunho.mapeia_para || "",
  );
  const avisoDoDestino = avisoDoDestinoGravado(rascunho.tipo, rascunho.mapeia_para || "");

  // ---- Opcoes ----
  const setOpcao = (idx, valor) =>
    set({ opcoes: rascunho.opcoes.map((o, i) => (i === idx ? valor : o)) });
  const addOpcao = () => set({ opcoes: [...rascunho.opcoes, ""] });
  const removeOpcao = (idx) =>
    set({ opcoes: rascunho.opcoes.filter((_, i) => i !== idx) });

  // ---- Limites ----
  const setLimite = (chave, valor) =>
    set({ validacao: { ...(rascunho.validacao || {}), [chave]: valor } });

  // ---- Condicao ----
  const habilitarCondicao = (on) => {
    if (on) {
      set({
        condicao: {
          acao: "MOSTRAR",
          modo: "TODAS",
          regras: camposReferenciaveis.length
            ? [
                {
                  campo: camposReferenciaveis[0]._key,
                  operador: "PREENCHIDO",
                  valor: "",
                },
              ]
            : [],
        },
      });
    } else {
      set({ condicao: null });
    }
  };

  const setCondicao = (patch) =>
    set({ condicao: { ...rascunho.condicao, ...patch } });

  const setRegra = (idx, patch) =>
    setCondicao({
      regras: rascunho.condicao.regras.map((r, i) =>
        i === idx ? { ...r, ...patch } : r
      ),
    });

  const addRegra = () =>
    setCondicao({
      regras: [
        ...rascunho.condicao.regras,
        {
          campo: camposReferenciaveis[0]?._key ?? "",
          operador: "PREENCHIDO",
          valor: "",
        },
      ],
    });

  const removeRegra = (idx) =>
    setCondicao({
      regras: rascunho.condicao.regras.filter((_, i) => i !== idx),
    });

  // ---- Salvar ----
  const handleSalvar = () => {
    setErro("");
    setAviso("");
    if (!ehSecao && !String(rascunho.label || "").trim()) {
      setErro("Informe o rotulo (label) do campo.");
      return;
    }
    if (ehSecao && !String(rascunho.label || "").trim()) {
      setErro("Informe o título da seção.");
      return;
    }
    if (temOpcoes) {
      const { opcoes: unicas, repetidas } = semOpcoesRepetidas(rascunho.opcoes);
      if (unicas.length === 0) {
        setErro("Adicione ao menos uma opção para este tipo de campo.");
        return;
      }
      // A repetida sai da lista e o painel continua aberto: quem digitou ve
      // o que saiu antes de confirmar (FORM-40). O servidor recusa repetida.
      if (repetidas.length > 0) {
        set({ opcoes: unicas });
        setAviso(
          `${repetidas.length > 1 ? "Removemos as opções repetidas" : "Removemos a opção repetida"} ${entreAspas(repetidas)}. Confira a lista e clique em Salvar campo de novo.`
        );
        return;
      }
    }
    const erroDosLimites = erroNosLimites(rascunho.tipo, rascunho.validacao);
    if (erroDosLimites) {
      setErro(erroDosLimites);
      return;
    }

    // Normaliza o objeto-campo de saida conforme o tipo.
    const limpo = {
      ...rascunho,
      label: String(rascunho.label || "").trim(),
      placeholder: ehSecao ? "" : String(rascunho.placeholder || ""),
      ajuda: String(rascunho.ajuda || ""),
      obrigatorio: ehSecao ? false : Boolean(rascunho.obrigatorio),
      largura: rascunho.largura === "HALF" ? "HALF" : "FULL",
      opcoes: temOpcoes
        ? rascunho.opcoes.map((o) => String(o).trim()).filter(Boolean)
        : [],
      mapeia_para: aceitaMapeia ? rascunho.mapeia_para || "" : "",
      validacao: limitesParaPayload(rascunho.tipo, rascunho.validacao),
    };

    // Regra sem campo valido nao some em silencio: ficava sempre falsa e o
    // campo condicional nunca aparecia. Sem nenhuma regra, a condicao e' {}.
    const regras = rascunho.condicao?.regras || [];
    const chavesReferenciaveis = new Set(camposReferenciaveis.map((c) => c._key));
    if (regras.some((r) => !chavesReferenciaveis.has(r.campo))) {
      setErro("Escolha, em cada regra, um campo do formulário.");
      return;
    }
    limpo.condicao =
      regras.length > 0
        ? { acao: rascunho.condicao.acao, modo: rascunho.condicao.modo, regras }
        : {};

    onSave(limpo);
  };

  // Modo leitura (so formulario:view): mostra a definicao, sem mudar (FORM-41).
  let titulo = ehSecao ? "Editar seção" : "Editar campo";
  if (somenteLeitura) titulo = ehSecao ? "Seção" : "Campo";

  const footer = somenteLeitura ? (
    <button type="button" className="btn btn--secondary" onClick={onClose}>
      Fechar
    </button>
  ) : (
    <>
      <button type="button" className="btn btn--secondary" onClick={onClose}>
        Cancelar
      </button>
      <button type="button" className="btn btn--primary" onClick={handleSalvar}>
        Salvar campo
      </button>
    </>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={titulo}
      subtitle={TIPO_CAMPO_LABEL[rascunho.tipo] || rascunho.tipo}
      width="min(96vw, 560px)"
      footer={footer}
    >
      <EditorFieldset disabled={somenteLeitura}>
        <PanelStack>
          {erro && (
            <div className="so-banner danger" role="alert">
              <span>{erro}</span>
            </div>
          )}
          {aviso && (
            <div className="so-banner warn" role="status">
              <span>{aviso}</span>
            </div>
          )}

          <PanelLabel>
            {ehSecao ? "Título da seção" : "Rotulo (label)"}
            <input
              className="input"
              type="text"
              value={rascunho.label || ""}
              maxLength={200}
              onChange={(e) => set({ label: e.target.value })}
              placeholder={ehSecao ? "Ex: Dados do projeto" : "Ex: Seu nome"}
              autoFocus
            />
          </PanelLabel>

          {!ehSecao && (
            <PanelLabel>
              Placeholder
              <input
                className="input"
                type="text"
                value={rascunho.placeholder || ""}
                maxLength={200}
                onChange={(e) => set({ placeholder: e.target.value })}
                placeholder="Texto de exemplo dentro do campo"
              />
            </PanelLabel>
          )}

          <PanelLabel>
            {ehSecao ? "Descrição (opcional)" : "Texto de ajuda"}
            <input
              className="input"
              type="text"
              value={rascunho.ajuda || ""}
              maxLength={300}
              onChange={(e) => set({ ajuda: e.target.value })}
              placeholder="Instrucao curta exibida abaixo do campo"
            />
          </PanelLabel>

          {!ehSecao && (
            <InlineRow>
              <CheckLabel>
                <input
                  type="checkbox"
                  checked={Boolean(rascunho.obrigatorio)}
                  onChange={(e) => set({ obrigatorio: e.target.checked })}
                />
                Obrigatório
              </CheckLabel>

              <PanelLabel style={{ marginLeft: "auto" }}>
                Largura
                <select
                  className="select"
                  value={rascunho.largura === "HALF" ? "HALF" : "FULL"}
                  onChange={(e) => set({ largura: e.target.value })}
                >
                  <option value="FULL">Largura total</option>
                  <option value="HALF">Meia largura</option>
                </select>
              </PanelLabel>
            </InlineRow>
          )}

          {/* ---- Limites de validacao (FORM-39) ---- */}
          {limites.length > 0 && (
            <div>
              <PanelSectionTitle>Limites</PanelSectionTitle>
              <InlineRow style={{ marginTop: 10 }}>
                {limites.map((chave) => (
                  <PanelLabel key={chave} style={{ flex: "1 1 140px" }}>
                    {ROTULO_DO_LIMITE[chave]}
                    <input
                      className="input"
                      type="number"
                      step={limitesDeTamanho ? 1 : "any"}
                      min={MINIMO_DO_LIMITE[chave]}
                      value={rascunho.validacao?.[chave] ?? ""}
                      onChange={(e) => setLimite(chave, e.target.value)}
                    />
                  </PanelLabel>
                ))}
              </InlineRow>
              <span className="input-help">{ajudaDosLimites(rascunho.tipo)}</span>
            </div>
          )}

          {/* ---- Mapeamento para o Lead ---- */}
          {aceitaMapeia && (
            <PanelLabel>
              Mapear para (campo do Lead)
              <select
                className="select"
                value={rascunho.mapeia_para || ""}
                onChange={(e) => set({ mapeia_para: e.target.value })}
              >
                <option value="">Não mapear (campo livre)</option>
                {grupos.map((g) => (
                  <optgroup key={g.grupo} label={g.grupo}>
                    {g.opcoes.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.desabilitada}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span className="input-help">
                Os nomes são os mesmos do card do lead no CRM. Para Origem,
                Temperatura e Status use uma lista/escolha com as opções do CRM
                (ex.: Quente, Morno, Frio); Tags aceita seleção múltipla. Cada
                destino é de um campo só — Observações e Bio aceitam vários, e
                as respostas entram uma por linha.
              </span>
              {/* Destino gravado que o tipo nao aceita: sem este aviso o
                  gestor so descobre no "Salvar campos", que recusa o
                  formulario inteiro (FORM-03). */}
              {avisoDoDestino && (
                <span className="input-help" role="alert" style={{ color: "var(--danger-text)" }}>
                  {avisoDoDestino}
                </span>
              )}
              {ajudaDoOcupado && <span className="input-help">{ajudaDoOcupado}</span>}
              {destinoEhEndereco(rascunho.mapeia_para) && (
                <span className="input-help">
                  {ajudaDoEndereco(rascunho.mapeia_para)}
                </span>
              )}
            </PanelLabel>
          )}

          {/* ---- Opcoes ---- */}
          {temOpcoes && (
            <div>
              <PanelSectionTitle>Opções</PanelSectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                {rascunho.opcoes.length === 0 && (
                  <SubLabel>Nenhuma opção ainda — adicione abaixo.</SubLabel>
                )}
                {rascunho.opcoes.map((op, idx) => (
                  <OpcaoRow key={idx}>
                    <input
                      className="input"
                      type="text"
                      value={op}
                      maxLength={150}
                      onChange={(e) => setOpcao(idx, e.target.value)}
                      placeholder={`Opcao ${idx + 1}`}
                      aria-label={`Opcao ${idx + 1}`}
                    />
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => removeOpcao(idx)}
                      aria-label={`Remover opcao ${idx + 1}`}
                    >
                      <FiTrash2 size={13} aria-hidden="true" />
                    </button>
                  </OpcaoRow>
                ))}
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={addOpcao}
                  style={{ alignSelf: "flex-start" }}
                >
                  <FiPlus size={13} aria-hidden="true" /> Adicionar opcao
                </button>
              </div>
            </div>
          )}

          {/* ---- Logica condicional ---- */}
          <div>
            <PanelSectionTitle>Logica condicional</PanelSectionTitle>
            <CheckLabel style={{ marginTop: 10 }}>
              <input
                type="checkbox"
                checked={temCondicao}
                onChange={(e) => habilitarCondicao(e.target.checked)}
                disabled={camposReferenciaveis.length === 0}
              />
              Mostrar/ocultar este campo conforme outras respostas
            </CheckLabel>

            {camposReferenciaveis.length === 0 && (
              <SubLabel style={{ marginTop: 6 }}>
                Adicione outros campos para poder criar regras condicionais.
              </SubLabel>
            )}

            {temCondicao && (
              <CondicaoBox style={{ marginTop: 12 }}>
                <InlineRow>
                  <select
                    className="select"
                    value={rascunho.condicao.acao}
                    onChange={(e) => setCondicao({ acao: e.target.value })}
                    aria-label="Ação da condicao"
                    style={{ width: "auto", flex: "1 1 160px" }}
                  >
                    {ACOES_CONDICAO.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: 13, color: "var(--ink-3)" }}>quando</span>
                  <select
                    className="select"
                    value={rascunho.condicao.modo}
                    onChange={(e) => setCondicao({ modo: e.target.value })}
                    aria-label="Modo de combinacao das regras"
                    style={{ width: "auto", flex: "1 1 160px" }}
                  >
                    {MODOS_CONDICAO.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
                    baterem:
                  </span>
                </InlineRow>

                {rascunho.condicao.regras.map((regra, idx) => {
                  const precisaValor = operadorPrecisaValor(regra.operador);
                  const referenciado = camposReferenciaveis.find((c) => c._key === regra.campo);
                  const opcoesDoReferenciado =
                    referenciado && tipoTemOpcoes(referenciado.tipo) && Array.isArray(referenciado.opcoes)
                      ? referenciado.opcoes
                      : null;
                  const opcaoDoValor = opcoesDoReferenciado?.find(
                    (op) => textoDaCondicao(op) === textoDaCondicao(regra.valor)
                  );
                  const valorForaDasOpcoes =
                    Boolean(opcoesDoReferenciado) && opcaoDoValor === undefined && Boolean(regra.valor);
                  return (
                    <RegraRow key={idx}>
                      <select
                        className="select"
                        value={referenciado ? regra.campo : ""}
                        onChange={(e) => setRegra(idx, { campo: e.target.value })}
                        aria-label="Campo de referencia"
                      >
                        {!referenciado && (
                          <option value="" disabled>
                            Escolha um campo
                          </option>
                        )}
                        {camposReferenciaveis.map((c) => (
                          <option key={c._key} value={c._key}>
                            {c.label || TIPO_CAMPO_LABEL[c.tipo]}
                          </option>
                        ))}
                      </select>
                      <select
                        className="select"
                        value={regra.operador}
                        onChange={(e) =>
                          setRegra(idx, { operador: e.target.value })
                        }
                        aria-label="Operador"
                      >
                        {OPERADORES_CONDICAO.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      {precisaValor && opcoesDoReferenciado ? (
                        // Campo de lista: o valor e' uma das opcoes dele, e nao
                        // texto livre que diverge por caixa ou digitacao (FORM-08).
                        <select
                          className="select"
                          value={opcaoDoValor ?? (regra.valor || "")}
                          onChange={(e) =>
                            setRegra(idx, { valor: e.target.value })
                          }
                          aria-label="Valor comparado"
                        >
                          <option value="">Escolha uma opção</option>
                          {valorForaDasOpcoes && (
                            <option value={regra.valor}>{`${regra.valor} (não é opção)`}</option>
                          )}
                          {opcoesDoReferenciado.map((op, i) => (
                            <option key={`${op}-${i}`} value={op}>
                              {op}
                            </option>
                          ))}
                        </select>
                      ) : precisaValor ? (
                        <input
                          className="input"
                          type="text"
                          value={regra.valor ?? ""}
                          onChange={(e) =>
                            setRegra(idx, { valor: e.target.value })
                          }
                          placeholder="Valor"
                          aria-label="Valor comparado"
                        />
                      ) : (
                        <span />
                      )}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => removeRegra(idx)}
                        aria-label="Remover regra"
                      >
                        <FiTrash2 size={13} aria-hidden="true" />
                      </button>
                    </RegraRow>
                  );
                })}

                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={addRegra}
                  style={{ alignSelf: "flex-start" }}
                  disabled={camposReferenciaveis.length === 0}
                >
                  <FiPlus size={13} aria-hidden="true" /> Adicionar regra
                </button>
              </CondicaoBox>
            )}
          </div>
        </PanelStack>
      </EditorFieldset>
    </SlideOverPanel>
  );
}

export default CampoEditorPanel;
